import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { SITE_ID, LOGO_URL } from '@/lib/constants';

interface Props {
  onLogin: () => void;
}

export default function TemaLogin({ onLogin }: Props) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError('Kullanıcı adı ve şifre gereklidir.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('siteler')
        .select('admin_kullanici_adi, admin_sifre')
        .eq('id', SITE_ID)
        .maybeSingle();

      if (err) throw err;
      if (!data) { setError('Site bulunamadı.'); return; }

      if (data.admin_kullanici_adi === username && data.admin_sifre === password) {
        sessionStorage.setItem('tema_auth', 'true');
        onLogin();
      } else {
        setError('Kullanıcı adı veya şifre hatalı.');
      }
    } catch {
      setError('Bağlantı hatası. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Logo" className="h-12 w-auto object-contain mx-auto mb-6" />
          <h1 className="text-xl font-semibold text-stone-800">Tema Yönetimi</h1>
          <p className="text-sm text-stone-500 mt-1">Admin bilgilerinizle giriş yapın</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 space-y-4">
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Kullanıcı Adı</label>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              placeholder="admin"
              autoComplete="username"
              className="w-full px-4 py-3 text-sm bg-stone-50 rounded-xl border border-stone-200 focus:border-amber-500 focus:outline-none focus:bg-white transition-all text-stone-800"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Şifre</label>
            <div className="relative">
              <input
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                className="w-full px-4 py-3 pr-11 text-sm bg-stone-50 rounded-xl border border-stone-200 focus:border-amber-500 focus:outline-none focus:bg-white transition-all text-stone-800"
              />
              <button
                type="button"
                onClick={() => setShowPass(!showPass)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-stone-400 hover:text-stone-600 transition-colors cursor-pointer"
              >
                <i className={showPass ? 'ri-eye-off-line' : 'ri-eye-line'} />
              </button>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600">
              <i className="ri-error-warning-line flex-shrink-0" />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-60 text-sm whitespace-nowrap cursor-pointer"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin" /> Giriş yapılıyor...
              </span>
            ) : 'Giriş Yap'}
          </button>
        </form>

        <p className="text-center text-xs text-stone-400 mt-6">
          Bu sayfa yalnızca site yöneticileri içindir.
        </p>
      </div>
    </div>
  );
}
